/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792627200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Oct 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "mntn",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to MNTN",
          "depth": 0,
          "id": "connect-to-mntn"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Segment",
          "depth": 1,
          "id": "segment"
        }, {
          "value": "Event",
          "depth": 1,
          "id": "event"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Behavior and limitations",
          "depth": 1,
          "id": "behavior-and-limitations"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      a: "a",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      h3: "h3",
      h4: "h4",
      code: "code",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "https://mountain.com/",
          children: "MNTN"
        }), " is a Performance TV advertising platform for running campaigns on connected TV. With the MNTN destination, you can build MNTN audience segments from your warehouse data to target or suppress on your campaigns, and send conversion events such as purchases and sign-ups to measure campaign performance."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#segment",
                children: _jsx(_components.strong, {
                  children: "Segment"
                })
              })
            }), _jsx(_components.td, {
              children: "Create MNTN audience segments and keep their membership up to date for targeting or suppression"
            }), _jsx(_components.td, {
              children: "Add, Remove"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://int-docs.ex.mountain.com/reference.html#tag/Audience",
                children: "Manage audience segments ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#event",
                children: _jsx(_components.strong, {
                  children: "Event"
                })
              })
            }), _jsx(_components.td, {
              children: "Send conversion events to MNTN for campaign measurement"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://int-docs.ex.mountain.com/reference.html#tag/Events",
                children: "Send conversion events ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-mntn",
        children: "Connect to MNTN"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch connects to MNTN with an Audience API key. One key covers both segment and event syncs."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The MNTN Audience API is currently in beta. Contact your MNTN account team to\nrequest access before creating an API key."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To create a key, sign in to MNTN, go to ", _jsx(_components.a, {
          href: "https://ui.mountain.com/myaccount/api",
          children: _jsx(_components.strong, {
            children: "My Account > API"
          })
        }), ", click ", _jsx(_components.strong, {
          children: "Connect"
        }), ", and create an ", _jsx(_components.strong, {
          children: "Audience API key"
        }), ". MNTN allows up to three Audience API keys per account, so you can keep Hightouch on its own key. Each key belongs to a single MNTN advertiser account, and every segment Hightouch creates or lists comes from that account."]
      }), "\n", _jsxs(_components.p, {
        children: ["Enter the key as the ", _jsx(_components.strong, {
          children: "Audience API key"
        }), " when you set up the destination in Hightouch. When you test the connection, Hightouch lists your MNTN segments to confirm the key works. If the test reports that the Audience API isn't enabled, contact your MNTN account team to enable it for your account."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your MNTN destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the MNTN destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "segment",
        children: "Segment"
      }), "\n", _jsx(_components.p, {
        children: "Use a segment sync to keep an MNTN audience segment matched to your model. Hightouch adds people when they enter your model and removes them when they leave, so the segment stays current between runs."
      }), "\n", _jsxs(_components.p, {
        children: ["To suppress people, such as existing subscribers or recent purchasers, sync them to a segment and add that segment as an exclusion on your MNTN campaigns. MNTN uses the same kind of segment for targeting and suppression, and you choose on each campaign whether it targets or excludes the segment. Retargeting campaigns can only use these segments as exclusions. See MNTN's guide to ", _jsx(_components.a, {
          href: "https://help.mountain.com/en/articles/6511947-source-audiences-with-crm",
          children: "sourcing audiences with CRM data"
        }), " for how segments apply to each campaign type."]
      }), "\n", _jsx(_components.h4, {
        id: "select-an-existing-segment-or-create-a-new-one",
        children: "Select an existing segment or create a new one"
      }), "\n", _jsxs(_components.p, {
        children: ["Choose ", _jsx(_components.strong, {
          children: "Create a new audience"
        }), " to have Hightouch create the segment on the first sync run. The segment uses the name you enter, or your model's name if you leave it blank, and is created with the ID ", _jsx(_components.code, {
          children: "hightouch-<your sync's ID>"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Choose ", _jsx(_components.strong, {
          children: "Use an existing audience"
        }), " to sync to a segment that already exists in your MNTN account, and select it from the list."]
      }), "\n", _jsx(_components.h4, {
        id: "map-identifiers",
        children: "Map identifiers"
      }), "\n", _jsx(_components.p, {
        children: "Map one or more identifier columns. MNTN matches each identifier to households, so mapping more identifiers reaches more of your audience. Hightouch formats each identifier the way MNTN expects before sending it:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Identifier"
            }), _jsx(_components.th, {
              children: "How Hightouch sends it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Email"
              })
            }), _jsx(_components.td, {
              children: "Trimmed, lowercased, and SHA-256 hashed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Phone"
              })
            }), _jsxs(_components.td, {
              children: ["Converted to E.164 digits with the country code and no ", _jsx(_components.code, {
                children: "+"
              }), ", then SHA-256 hashed. Numbers without a country code use the default country you select."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "IP address"
              })
            }), _jsx(_components.td, {
              children: "Sent as the raw IPv4 or IPv6 address"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Mobile advertising ID (MAID)"
              })
            }), _jsxs(_components.td, {
              children: ["Sent unhashed as a lowercase UUID, such as ", _jsx(_components.code, {
                children: "8047992a-e64e-4e4a-9b48-18e0018a1670"
              })]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch hashes emails and phone numbers automatically. Values that are already SHA-256 hashed are sent as they are, so you can mix hashed and unhashed columns. If you set ", _jsx(_components.strong, {
          children: "Would you like Hightouch to automatically detect if your PII data requires hashing?"
        }), " to ", _jsx(_components.strong, {
          children: "No"
        }), ", Hightouch sends unhashed emails and phone numbers in plain text."]
      }), "\n", _jsx(_components.p, {
        children: "An identifier that isn't valid, such as a malformed phone number, IP address, or MAID, is left out, and the rest of the row's identifiers still sync. Hightouch only rejects a row when it has no usable identifier at all."
      }), "\n", _jsx(_components.h4, {
        id: "configure-delete-behavior",
        children: "Configure delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["Keep ", _jsx(_components.strong, {
          children: "Remove the MNTN record from the specified audience"
        }), " selected so people who leave your model are removed from the segment. For a suppression segment, this lets people back into your campaigns when they no longer qualify, for example after a subscription ends. Choose ", _jsx(_components.strong, {
          children: "Do nothing"
        }), " only when the segment should keep growing and never drop anyone."]
      }), "\n", _jsx(_components.h3, {
        id: "event",
        children: "Event"
      }), "\n", _jsx(_components.p, {
        children: "Use an event sync to send conversions, such as purchases or sign-ups, to MNTN server-to-server."
      }), "\n", _jsx(_components.h4, {
        id: "event-properties",
        children: "Event properties"
      }), "\n", _jsx(_components.p, {
        children: "Configure these fields for each event:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event name"
          }), ": A column or static value naming the conversion, such as ", _jsx(_components.code, {
            children: "Purchase"
          }), " or ", _jsx(_components.code, {
            children: "Start Trial"
          }), ". Event names can be up to 128 printable ASCII characters."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Timestamp"
          }), ": The column containing the time the conversion happened. Hightouch keeps sub-second precision."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identifiers"
          }), ": The same identifiers as segment syncs, formatted and hashed the same way. Map at least one so MNTN can attribute the conversion."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "You can also map these optional fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event ID"
          }), ": A unique ID for the event, which MNTN uses to de-duplicate events sent more than once. Map it whenever your model has one."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Order ID"
          }), ", ", _jsx(_components.strong, {
            children: "Order amount"
          }), ", and ", _jsx(_components.strong, {
            children: "Currency (ISO 4217 code)"
          }), ": Order details for purchases. The currency must be a three-letter code such as ", _jsx(_components.code, {
            children: "USD"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Hightouch rejects an event when it has no usable identifier, its event name is empty or too long, its timestamp is empty, its order amount isn't a number, or its currency isn't a three-letter code."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to MNTN, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes: Other\ndestinations"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "behavior-and-limitations",
        children: "Behavior and limitations"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "MNTN doesn't report per-person results."
          }), " MNTN processes additions, removals, and events after accepting them, and doesn't report which people it matched. A successful sync run means MNTN accepted the data, not that every person matched a household."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Each sync manages its own segment members."
          }), " Hightouch identifies people in a segment by your sync's ID and your model's primary key, so two syncs can share a segment without overwriting each other. If you delete a sync and create a new one for the same existing segment, the old sync's members stay in the segment. To start from an empty segment, sync to a new one instead."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Run segment syncs on a schedule."
          }), " MNTN recommends refreshing segments daily or weekly so they stay current. Let your MNTN account team know which schedule you choose so they can monitor it."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first segment sync completes, open your audience list in MNTN and find the segment by its name. MNTN lists new segments within minutes, and the segment is then available to add to campaigns as a target or an exclusion."
      }), "\n", _jsx(_components.p, {
        children: "For event syncs, MNTN accepts conversions without returning per-event results to Hightouch. Confirm with your MNTN account team that conversions are arriving and attributed to your campaigns."
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
